import { Breadcrumb, Button, Checkbox, Select, Tooltip } from 'antd'
import React from 'react'
import { buildPath, displayFolderName } from '../../dux/modules/utils'
import TwoButtonContainer from '../BAM_UI/TwoButtonContainer'
import {
  EDITABLE_SYSTEM_GROUP,
  getImplicitRoles,
  isLockedEntity,
  isSystemGroup,
} from '../permissions/utils/permissionsUtils'

const Option = Select.Option

export const generateColumns = (
  roles,
  sysGroupStatus,
  toggleSysGroups,
  selectAllEntities,
) => {
  const columns = [
    {
      title: <Checkbox onChange={selectAllEntities} />,
      dataIndex: 'selector',
      className: 'modal__permissions-col-hdr',
      key: 'selector',
    },
    {
      title: (
        <span>
          <div>User/Group</div>
          <div className="permissions__linkspan" onClick={toggleSysGroups}>
            {sysGroupStatus === 'show'
              ? 'Hide System Groups'
              : 'Show System Groups'}
          </div>
        </span>
      ),
      dataIndex: 'name',
      className: 'modal__permissions-col-hdr',
      key: 'name',
    },
  ]
  return columns.concat(
    roles
      .filter((role) => role.contentRole)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((role) => {
        return {
          title: <Tooltip title={role.description}>{role.displayName}</Tooltip>,
          dataIndex: role.id,
          className: 'modal__permissions-col-hdr',
          key: role.id,
          width: 75,
          render: (text, record) => {
            const box = (
              <Checkbox
                checked={record[role.id]}
                disabled={record.locked || role.id === 'traverser'}
                onChange={(e) => record.handleChange(e, record, role.id)}
              />
            )
            if (!record.locked) return box
            return (
              <Tooltip
                title={
                  record.implicitRoles.indexOf(role.id) > -1
                    ? 'Granted by membership of this system group'
                    : 'System group permissions cannot be changed'
                }
              >
                {box}
              </Tooltip>
            )
          },
        }
      }),
  )
}

const sortEntityList = (a, b, groups, users) => {
  if (!groups[a] && groups[b]) return -1
  if (!groups[b] && groups[a]) return 1
  if (!users[a] && users[b]) return -1
  if (!users[b] && users[a]) return 1
  // Neither is a known group: either both are users, or a folder still names
  // a group that has been deleted, in which case sort by the raw id.
  if (!groups[a] && !groups[b])
    return users[a] && users[b]
      ? users[a].name.localeCompare(users[b].name)
      : String(a).localeCompare(String(b))
  if (groups[a].system && !groups[b].system) return 1
  if (!groups[a].system && groups[b].system) return -1
  return groups[a].name.localeCompare(groups[b].name)
}

export const formatDataSource = (
  contentRoles,
  groups,
  users,
  hideSysGroups,
  handleChange,
  selectedEntities,
  onEntityClick,
  roles = [],
  inPublished = false,
) => {
  let datasource = []
  // System groups with access by membership get a row even when the folder
  // has no contentRoles entry for them. These rows are display-only: they are
  // never added to contentRoles, so they are never saved.
  const implicit = {}
  Object.keys(groups)
    .filter(isSystemGroup)
    .forEach((entity) => {
      const roleIds = getImplicitRoles(entity, roles, inPublished)
      if (roleIds.length) implicit[entity] = roleIds
    })
  const rows = { ...contentRoles }
  Object.keys(implicit).forEach((entity) => {
    if (!rows[entity])
      rows[entity] = { entityType: 'group', entityId: entity, roleIds: [] }
  })

  Object.keys(rows)
    .filter((entity) => !(hideSysGroups && isSystemGroup(entity)))
    .sort((a, b) => sortEntityList(a, b, groups, users))
    .forEach((entity) => {
      const locked = isLockedEntity(entity)
      const implicitRoles = implicit[entity] || []
      const has = (role) =>
        rows[entity].roleIds.indexOf(role) > -1 ||
        implicitRoles.indexOf(role) > -1
      datasource.push({
        key: entity,
        locked,
        implicitRoles,
        selector: (
          <Checkbox
            checked={selectedEntities.indexOf(entity) > -1}
            disabled={locked}
            onChange={() => onEntityClick(entity)}
          />
        ),
        name: getEntityName(entity, rows, groups),
        mobileuser: has('mobileuser'),
        viewer: has('viewer'),
        creator: has('creator'),
        editor: has('editor'),
        publisher: has('publisher'),
        approver: has('approver'),
        contentadmin: has('contentadmin'),
        traverser: has('traverser'),
        handleChange,
      })
    })
  return datasource
}

export const getEntityName = (entity, contentRoles, groups) => {
  let name = ''
  if (contentRoles[entity].entityType === 'group') {
    name = (groups[entity] && groups[entity].name) || entity
  } else {
    name = entity
  }
  return name
}

// This function return an array filter that filters an object for keys that match role ids
export const validRoles = (roles) => {
  return (item) => roles.some((role) => role.id === item)
}

export const getFooter = (
  saveFunc,
  cancelFunc,
  removeFunc,
  selectedEntities,
  primaryDisabled,
  extra = null, // e.g. the "apply to subfolders" option
) => {
  return (
    <span className="modal__flex">
      <span>
        <Button onClick={removeFunc} disabled={selectedEntities.length < 1}>
          Remove Selected
        </Button>
        {extra}
      </span>
      <span>
        <TwoButtonContainer
          primaryButtonText="OK"
          primaryButtonAction={saveFunc}
          primaryButtonDisabled={primaryDisabled}
          secondaryButtonText="Cancel"
          secondaryButtonAction={cancelFunc}
        />
      </span>
    </span>
  )
}

/*
 * Build modal title with path of folder being edited
 * options arg looks like: {
 *   groups,
 *   users,
 *   activeFolder,
 *   allFolders,
 *   newFolder,
 *   newFolderName,
 *   groupSelect,
 *   userSelect,
 *   contentRoles,
 *   addUser,
 *   addGroup,
 *   showSysGroups,
 * }
 */
export const getModalTitle = (options) => {
  const {
    groups,
    users,
    activeFolder,
    allFolders,
    newFolder,
    newFolderName,
    groupSelect,
    userSelect,
    contentRoles,
    addUser,
    addGroup,
    showSysGroups,
  } = options
  return (
    <span className="modal__flex">
      <span>
        <span> Set Folder Permissions: &nbsp;</span>
        <span>
          <Breadcrumb separator=">">
            {buildPath(activeFolder, allFolders)
              .reverse()
              .map((crumb) => (
                <Breadcrumb.Item key={crumb}>{` ${displayFolderName(
                  allFolders[crumb].name,
                )} `}</Breadcrumb.Item>
              ))
              .concat(
                newFolder
                  ? [
                      <Breadcrumb.Item key="new">{` ${newFolderName} `}</Breadcrumb.Item>,
                    ]
                  : null,
              )}
          </Breadcrumb>
        </span>
      </span>
      <span>
        <Select
          id="groupselect"
          key={'group'}
          showSearch
          defaultActiveFirstOption={false}
          style={{ width: 175, margin: '10px 0 10px 5px' }}
          placeholder="Add a Group"
          filterOption
          optionFilterProp="name"
          onChange={addGroup}
          value={groupSelect}
          notFoundContent="No groups available to add"
        >
          {Object.keys(groups)
            .filter((grp) => !isLockedEntity(grp))
            .filter((grp) =>
              // "System Groups" adds All Users if it is missing and reveals
              // the system group rows, so it stays useful until both are true
              grp === EDITABLE_SYSTEM_GROUP
                ? !contentRoles[grp] || !showSysGroups
                : !contentRoles[grp],
            )
            .map((grp) => ({
              grp,
              name:
                grp === EDITABLE_SYSTEM_GROUP
                  ? 'System Groups'
                  : groups[grp].name,
            }))
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(({ grp, name }) => (
              <Option key={grp} name={name}>
                {name}
              </Option>
            ))}
        </Select>
        <Select
          id="userselect"
          key={'user'}
          showSearch
          defaultActiveFirstOption={false}
          style={{ width: 175, margin: '10px 0 10px 5px' }}
          placeholder="Add a User"
          filterOption
          optionFilterProp="email"
          onChange={addUser}
          value={userSelect}
          notFoundContent="No users available to add"
        >
          {Object.keys(users)
            .sort((a, b) => users[a].email.localeCompare(users[b].email))
            .filter((u) => !contentRoles[u])
            .map((u) => {
              return (
                <Option key={u} email={users[u].email}>
                  {users[u].email}
                </Option>
              )
            })}
        </Select>
      </span>
    </span>
  )
}
